'use client'

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { useRouter } from 'next/navigation'
import {
  clearAppAuthSession,
  getAppRefreshToken,
  getFreshAppAccessToken,
} from '@/lib/app-auth-session'
import { appLog } from '@/lib/logger'

interface SignOutOptions {
  /** If provided, router.replace is called with this path after sign-out. */
  returnTo?: string
}

/**
 * Minimal user shape (id, email, …) returned by FastAPI on login / refresh.
 * Only fields we actually populate are non-optional.
 */
export interface AuthUser {
  id: string
  email?: string
  user_metadata?: Record<string, unknown>
  app_metadata?: Record<string, unknown>
  aud?: string
  role?: string
  created_at?: string
}

/**
 * Minimal session shape returned by FastAPI on login / refresh.
 */
export interface AuthSession {
  access_token: string
  refresh_token: string
  expires_in: number
  expires_at: number
  token_type: 'bearer'
  user: AuthUser
}

type AuthContextType = {
  user: AuthUser | null
  session: AuthSession | null
  loading: boolean
  signOut: (options?: SignOutOptions) => Promise<void>
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  signOut: async () => {},
})

function buildSessionFromAccessToken(access: string): AuthSession | null {
  try {
    const payload = JSON.parse(atob(access.split('.')[1])) as {
      sub?: string
      email?: string | null
      exp?: number
    }
    const uid = payload.sub ?? ''
    if (!uid) return null
    const email = payload.email ?? ''
    const exp = payload.exp ?? Math.floor(Date.now() / 1000) + 3600
    const refresh = getAppRefreshToken() ?? ''
    return {
      access_token: access,
      refresh_token: refresh,
      expires_in: Math.max(60, exp - Math.floor(Date.now() / 1000)),
      expires_at: exp,
      token_type: 'bearer',
      user: {
        id: uid,
        email: email || undefined,
        aud: 'authenticated',
        role: 'authenticated',
        app_metadata: {},
        user_metadata: {},
        created_at: new Date().toISOString(),
      },
    }
  } catch {
    return null
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<AuthSession | null>(null)
  const [loading, setLoading] = useState(true)
  const router = useRouter()

  const syncSession = useCallback(async () => {
    const t = await getFreshAppAccessToken()
    if (!t) {
      setSession(null)
      return
    }
    setSession(buildSessionFromAccessToken(t))
  }, [])

  useEffect(() => {
    const onAppAuth = () => {
      void syncSession()
    }
    window.addEventListener('fh-app-auth-changed', onAppAuth)
    queueMicrotask(() => {
      void syncSession().finally(() => setLoading(false))
    })
    const id = window.setInterval(() => void syncSession(), 60_000)
    return () => {
      window.removeEventListener('fh-app-auth-changed', onAppAuth)
      window.clearInterval(id)
    }
  }, [syncSession])

  const signOut = async (options?: SignOutOptions) => {
    appLog.debug('auth signOut', { returnTo: options?.returnTo ?? null })
    clearAppAuthSession()
    setSession(null)
    if (options?.returnTo) {
      router.replace(options.returnTo)
    }
  }

  return (
    <AuthContext.Provider
      value={{
        user: session?.user ?? null,
        session,
        loading,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
